/**
 * MatrixRenderer — the matrix / heatmap lens.
 *
 * Renders a grid of cells (rows × cols) where each cell's fill encodes a value.
 * Rows and columns are placed with d3-scaleBand; the fill ramps from the theme
 * background toward palette[0] (interpolating two palette colors for extra
 * range) by normalized value. Top + left margins are reserved for the header
 * labels — column labels are rotated ~-45° so long headers stay legible without
 * overlap. All geometry (margins, label widths, rotation offsets) is computed
 * arithmetically so the renderer runs headless under jsdom (no getBBox / DOM
 * text measurement).
 *
 * Dark theme: luminous cells on near-black, value text in light ink for dense
 * cells. Editorial theme: ink-on-warm cells with a hairline border on every
 * cell (theme.node.stroke) and dark value text.
 */

import { scaleBand } from "d3-scale";
import { select, type Selection } from "d3-selection";

import { THEMES, type Theme } from "../theme.js";
import type {
  LensExample,
  LensRenderer,
  LensRendererOptions,
} from "../lens.js";

interface MatrixAxisItem {
  id: string;
  label: string;
}
interface MatrixCell {
  row: string;
  col: string;
  value: number;
}
interface MatrixData {
  title: string;
  rows: MatrixAxisItem[];
  cols: MatrixAxisItem[];
  cells: MatrixCell[];
}

const SVG_NS = "http://www.w3.org/2000/svg";

export class MatrixRenderer implements LensRenderer {
  private readonly svg: Selection<SVGSVGElement, unknown, null, undefined>;
  private readonly width: number;
  private readonly height: number;
  private readonly theme: Theme;
  private readonly showLabels: boolean;

  private data: MatrixData | null = null;

  constructor(
    container: SVGSVGElement | HTMLElement,
    options: LensRendererOptions = {},
  ) {
    this.width = options.width ?? 960;
    this.height = options.height ?? 720;
    this.theme =
      typeof options.theme === "object"
        ? options.theme
        : THEMES[options.theme ?? "dark"];
    this.showLabels = options.showLabels ?? true;

    if (container instanceof SVGSVGElement) {
      this.svg = select(container);
    } else {
      const svgEl = document.createElementNS(SVG_NS, "svg");
      container.appendChild(svgEl);
      this.svg = select(svgEl as SVGSVGElement);
    }

    this.svg
      .attr("width", this.width)
      .attr("height", this.height)
      .attr("viewBox", `0 0 ${this.width} ${this.height}`)
      .attr("preserveAspectRatio", "xMidYMid meet")
      .style("background", this.theme.background);
  }

  render(data: unknown): this {
    this.data = normalize(data);
    this.draw();
    return this;
  }

  /** Static layout — nothing to settle. */
  settle(_ticks?: number): this {
    return this;
  }

  exportSVG(): string {
    const node = this.svg.node();
    if (!node) return "";
    const clone = node.cloneNode(true) as SVGSVGElement;
    return new XMLSerializer().serializeToString(clone);
  }

  destroy(): void {
    this.svg.selectAll("*").remove();
  }

  // ── internals ─────────────────────────────────────────────────────────

  private draw(): void {
    this.svg.selectAll("*").remove();
    if (!this.data) return;
    const theme = this.theme;
    const { rows, cols, cells, title } = this.data;

    // Background rect so exported SVGs carry the field with them.
    this.svg
      .append("rect")
      .attr("width", this.width)
      .attr("height", this.height)
      .attr("fill", theme.background);

    const fs = theme.label.fontSize;
    const titleSize = Math.round(fs * 1.9);

    // ── Margins, computed arithmetically ──────────────────────────────────
    // Left margin holds horizontal row labels; size it to the widest label.
    const longestRow = Math.max(0, ...rows.map((r) => r.label.length));
    const rowLabelW = longestRow * fs * 0.6;
    const padLeft = Math.min(220, Math.max(70, rowLabelW + 20));

    // Top margin holds column labels rotated -45°; their vertical footprint is
    // the label length projected onto the diagonal (sin 45° ≈ 0.707).
    const longestCol = Math.max(0, ...cols.map((c) => c.label.length));
    const colLabelLen = longestCol * fs * 0.6;
    // The title gets its own band at the very top; the rotated column headers
    // live below it so the two never collide.
    const titleBand = title ? titleSize + 22 : 0;
    const padTop = Math.min(
      240,
      Math.max(64, colLabelLen * 0.707 + titleBand + 24),
    );

    const padRight = 56;
    const padBottom = 64;

    const gridW = Math.max(10, this.width - padLeft - padRight);
    const gridH = Math.max(10, this.height - padTop - padBottom);

    const x = scaleBand<string>()
      .domain(cols.map((c) => c.id))
      .range([0, gridW])
      .paddingInner(0.06)
      .paddingOuter(0.03);

    const y = scaleBand<string>()
      .domain(rows.map((r) => r.id))
      .range([0, gridH])
      .paddingInner(0.06)
      .paddingOuter(0.03);

    const cellW = x.bandwidth();
    const cellH = y.bandwidth();

    // Normalize values to 0..1 across all cells.
    const values = cells.map((c) => c.value);
    const minV = values.length ? Math.min(...values) : 0;
    const maxV = values.length ? Math.max(...values) : 1;
    const span = maxV - minV || 1;
    const norm = (v: number) => (v - minV) / span;

    // Color ramp: background → palette[1] → palette[0]. Interpolating two
    // palette colors gives a richer, intentional gradient than a single hue.
    const lo = theme.background;
    const mid = theme.palette[Math.min(1, theme.palette.length - 1)];
    const hi = theme.palette[0];
    const fillFor = (t: number) => {
      // Snap the extremes to the exact palette strings (preserves source casing
      // so palette-color assertions / theme tooling match precisely).
      if (t >= 0.999) return hi;
      if (t <= 0.001) return lo;
      return t < 0.5
        ? lerpColor(lo, mid, t / 0.5)
        : lerpColor(mid, hi, (t - 0.5) / 0.5);
    };

    // Title.
    this.svg
      .append("text")
      .attr("x", padLeft)
      .attr("y", titleSize + 2)
      .attr("font-family", theme.label.fontFamily)
      .attr("font-size", titleSize)
      .attr("font-weight", 600)
      .attr("fill", theme.label.color)
      .text(title);

    const content = this.svg
      .append("g")
      .attr("class", "content")
      .attr("transform", `translate(${padLeft},${padTop})`);

    // Cell value lookup.
    const cellByKey = new Map<string, number>();
    for (const c of cells) cellByKey.set(`${c.row} ${c.col}`, c.value);

    // ── Cells ─────────────────────────────────────────────────────────────
    const cellG = content.append("g").attr("class", "cells");
    for (const r of rows) {
      const cy = y(r.id) ?? 0;
      for (const c of cols) {
        const cx = x(c.id) ?? 0;
        const raw = cellByKey.get(`${r.id} ${c.id}`);
        const has = raw !== undefined;
        const t = has ? norm(raw as number) : 0;
        const fill = has ? fillFor(t) : theme.background;

        const rect = cellG
          .append("rect")
          .attr("x", cx)
          .attr("y", cy)
          .attr("width", cellW)
          .attr("height", cellH)
          .attr("rx", 2)
          .attr("fill", fill)
          .attr("fill-opacity", has ? 1 : 0.25);

        // Hairline border: always in editorial; subtle grid in dark.
        if (theme.node.stroke) {
          rect
            .attr("stroke", theme.node.stroke)
            .attr("stroke-width", theme.node.strokeWidth ?? 1);
        } else {
          rect.attr("stroke", "rgba(255,255,255,0.06)").attr("stroke-width", 1);
        }

        // Value text in dense/strong cells, where it reads with contrast.
        if (has && this.showLabels && cellW >= 26 && cellH >= 16) {
          const strong = t >= 0.55;
          const textColor = textColorFor(theme, strong);
          cellG
            .append("text")
            .attr("x", cx + cellW / 2)
            .attr("y", cy + cellH / 2)
            .attr("text-anchor", "middle")
            .attr("dominant-baseline", "central")
            .attr("font-family", theme.label.valueFontFamily ?? theme.label.fontFamily)
            .attr("font-size", Math.min(fs + 1, cellH * 0.5))
            .attr("fill", textColor)
            .text(formatValue(raw as number, minV, maxV));
        }
      }
    }

    if (!this.showLabels) return;

    // ── Row labels (left, horizontal, right-aligned to grid edge) ─────────
    const rowG = this.svg
      .append("g")
      .attr("class", "row-labels")
      .attr("transform", `translate(${padLeft},${padTop})`);
    for (const r of rows) {
      const cy = (y(r.id) ?? 0) + cellH / 2;
      rowG
        .append("text")
        .attr("x", -10)
        .attr("y", cy)
        .attr("text-anchor", "end")
        .attr("dominant-baseline", "central")
        .attr("font-family", theme.label.fontFamily)
        .attr("font-size", fs + 1)
        .attr("fill", theme.label.color)
        .attr("paint-order", "stroke")
        .attr("stroke", theme.background)
        .attr("stroke-width", 2.5)
        .attr("stroke-linejoin", "round")
        .text(r.label);
    }

    // ── Column labels (top, rotated -45°) ─────────────────────────────────
    const colG = this.svg
      .append("g")
      .attr("class", "col-labels")
      .attr("transform", `translate(${padLeft},${padTop})`);
    for (const c of cols) {
      const cx = (x(c.id) ?? 0) + cellW / 2;
      colG
        .append("text")
        .attr("font-family", theme.label.fontFamily)
        .attr("font-size", fs + 1)
        .attr("fill", theme.label.color)
        .attr("text-anchor", "start")
        .attr("paint-order", "stroke")
        .attr("stroke", theme.background)
        .attr("stroke-width", 2.5)
        .attr("stroke-linejoin", "round")
        .attr("transform", `translate(${cx},-8) rotate(-45)`)
        .text(c.label);
    }
  }
}

// ── pure helpers ──────────────────────────────────────────────────────────

function normalize(data: unknown): MatrixData {
  const d = (data ?? {}) as Partial<MatrixData>;
  return {
    title: typeof d.title === "string" ? d.title : "Matrix",
    rows: Array.isArray(d.rows) ? d.rows : [],
    cols: Array.isArray(d.cols) ? d.cols : [],
    cells: Array.isArray(d.cells) ? d.cells : [],
  };
}

/** High-contrast value text: light ink on strong dark-theme cells, dark on light. */
function textColorFor(theme: Theme, strong: boolean): string {
  if (theme.name === "editorial") {
    return strong ? "#FAF8F5" : "rgba(26,26,26,0.85)";
  }
  return strong ? "rgba(255,255,255,0.95)" : "rgba(255,255,255,0.6)";
}

function formatValue(v: number, minV: number, maxV: number): string {
  // If the data already looks like a 0..1 fraction, show a percent.
  if (minV >= 0 && maxV <= 1) return `${Math.round(v * 100)}`;
  if (Number.isInteger(v)) return String(v);
  return v.toFixed(1);
}

/** Linear interpolate between two #rrggbb (or rgb) colors → #rrggbb. */
function lerpColor(a: string, b: string, t: number): string {
  const ca = parseHex(a);
  const cb = parseHex(b);
  const k = Math.max(0, Math.min(1, t));
  const r = Math.round(ca[0] + (cb[0] - ca[0]) * k);
  const g = Math.round(ca[1] + (cb[1] - ca[1]) * k);
  const bch = Math.round(ca[2] + (cb[2] - ca[2]) * k);
  return `#${toHex(r)}${toHex(g)}${toHex(bch)}`;
}

function parseHex(hex: string): [number, number, number] {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (m) {
    const n = parseInt(m[1], 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }
  const s = /^#?([0-9a-f]{3})$/i.exec(hex.trim());
  if (s) {
    const r = parseInt(s[1][0] + s[1][0], 16);
    const g = parseInt(s[1][1] + s[1][1], 16);
    const b = parseInt(s[1][2] + s[1][2], 16);
    return [r, g, b];
  }
  return [0, 0, 0];
}

function toHex(n: number): string {
  return Math.max(0, Math.min(255, n)).toString(16).padStart(2, "0");
}

// ── examples ────────────────────────────────────────────────────────────

export const MATRIX_EXAMPLES: LensExample[] = [
  {
    id: "matrix-agent-capability",
    title: "Agent × Capability Coverage",
    blurb:
      "Where each Totem agent is strong, partial, or absent across the core capability stack.",
    highlights: ["coverage", "multi-agent", "0–1 ramp"],
    lens: "matrix",
    build: (): MatrixData => {
      const rows: MatrixAxisItem[] = [
        { id: "logger", label: "Message Logger" },
        { id: "link", label: "Link Processor" },
        { id: "classifier", label: "Content Classifier" },
        { id: "bridge", label: "Context Bridge" },
        { id: "action", label: "Action Extractor" },
        { id: "synth", label: "Insight Synthesizer" },
        { id: "kg", label: "Knowledge Graph" },
        { id: "factory", label: "Content Factory" },
      ];
      const cols: MatrixAxisItem[] = [
        { id: "memory", label: "Memory" },
        { id: "tools", label: "Tool Use" },
        { id: "planning", label: "Planning" },
        { id: "synthesis", label: "Synthesis" },
        { id: "retrieval", label: "Retrieval" },
        { id: "routing", label: "Routing" },
      ];
      // Hand-tuned so the heatmap has a readable structure (specialists vs. generalists).
      const v: Record<string, number[]> = {
        logger: [0.9, 0.35, 0.1, 0.05, 0.7, 0.4],
        link: [0.4, 0.85, 0.3, 0.2, 0.75, 0.55],
        classifier: [0.55, 0.5, 0.45, 0.6, 0.65, 0.8],
        bridge: [0.95, 0.6, 0.55, 0.7, 0.85, 0.9],
        action: [0.5, 0.7, 0.9, 0.45, 0.4, 0.5],
        synth: [0.7, 0.45, 0.65, 0.95, 0.6, 0.35],
        kg: [0.85, 0.55, 0.4, 0.8, 0.95, 0.45],
        factory: [0.45, 0.75, 0.6, 0.85, 0.5, 0.3],
      };
      const cells: MatrixCell[] = [];
      for (const r of rows) {
        const row = v[r.id];
        cols.forEach((c, i) => cells.push({ row: r.id, col: c.id, value: row[i] }));
      }
      return { title: "Agent × Capability Coverage", rows, cols, cells };
    },
  },
  {
    id: "matrix-lens-usecase",
    title: "Lens × Use-case Fit",
    blurb:
      "How well each of the six visualization lenses fits common analysis use-cases.",
    highlights: ["6 lenses", "fit score", "design"],
    lens: "matrix",
    build: (): MatrixData => {
      const rows: MatrixAxisItem[] = [
        { id: "force", label: "Force-Directed" },
        { id: "sankey", label: "Sankey / Flow" },
        { id: "timeline", label: "Timeline" },
        { id: "matrix", label: "Matrix / Heatmap" },
        { id: "tree", label: "Tree / Hierarchy" },
        { id: "radar", label: "Radar / Spider" },
      ];
      const cols: MatrixAxisItem[] = [
        { id: "topology", label: "Topology" },
        { id: "flow", label: "Flow / Throughput" },
        { id: "time", label: "Temporal" },
        { id: "compare", label: "Cross-compare" },
        { id: "hierarchy", label: "Hierarchy" },
        { id: "profile", label: "Profiles" },
      ];
      const v: Record<string, number[]> = {
        force: [0.98, 0.4, 0.25, 0.45, 0.55, 0.3],
        sankey: [0.5, 0.98, 0.5, 0.45, 0.6, 0.2],
        timeline: [0.2, 0.45, 0.98, 0.4, 0.3, 0.25],
        matrix: [0.55, 0.35, 0.3, 0.98, 0.4, 0.6],
        tree: [0.6, 0.5, 0.35, 0.4, 0.98, 0.3],
        radar: [0.35, 0.25, 0.2, 0.65, 0.35, 0.98],
      };
      const cells: MatrixCell[] = [];
      for (const r of rows) {
        const row = v[r.id];
        cols.forEach((c, i) => cells.push({ row: r.id, col: c.id, value: row[i] }));
      }
      return { title: "Lens × Use-case Fit", rows, cols, cells };
    },
  },
  {
    id: "matrix-skill-map",
    title: "Skill Map",
    blurb:
      "Skill depth across personas and collaborators against the domains Sense Collective works in.",
    highlights: ["people", "domains", "depth"],
    lens: "matrix",
    build: (): MatrixData => {
      const rows: MatrixAxisItem[] = [
        { id: "jonny", label: "Jonny" },
        { id: "sense", label: "SenseCollective" },
        { id: "technical", label: "Technical" },
        { id: "educational", label: "Educational" },
        { id: "shurai", label: "ShurAI" },
        { id: "totem", label: "Totem Architect" },
      ];
      const cols: MatrixAxisItem[] = [
        { id: "agents", label: "Multi-agent" },
        { id: "kg", label: "Knowledge Graph" },
        { id: "crypto", label: "Crypto / Trading" },
        { id: "iot", label: "IoT Sensors" },
        { id: "content", label: "Content" },
        { id: "teaching", label: "Teaching" },
      ];
      const v: Record<string, number[]> = {
        jonny: [0.7, 0.85, 0.8, 0.5, 0.6, 0.55],
        sense: [0.6, 0.9, 0.3, 0.4, 0.55, 0.7],
        technical: [0.95, 0.8, 0.65, 0.7, 0.35, 0.4],
        educational: [0.4, 0.55, 0.2, 0.25, 0.6, 0.95],
        shurai: [0.45, 0.4, 0.2, 0.15, 0.95, 0.5],
        totem: [0.98, 0.9, 0.55, 0.45, 0.5, 0.45],
      };
      const cells: MatrixCell[] = [];
      for (const r of rows) {
        const row = v[r.id];
        cols.forEach((c, i) => cells.push({ row: r.id, col: c.id, value: row[i] }));
      }
      return { title: "Skill Map", rows, cols, cells };
    },
  },
];
